import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { format, parseISO } from 'date-fns';

import { useCurrentLocale, useScopedI18n } from '@locales/client';
import { StyledAlertDialogOverlay } from '@/shared/ui/alert-dialog/styles';
import { useCashbackHistory } from '../../model/hooks';
import { getDateLocale } from '@/shared/libs/locale';
import { ShowOnMobile, ShowOnDesktop } from '@/shared/ui/responsive';
import {
  BodyTextBoldSecondary,
  MediumHeadingQuinary,
} from '@/shared/ui/typography';

import {
  CloseIconImage,
  HistoryIconImage,
  DateStyleDesktop,
  DateStyleMobile,
  Details,
  Item,
  ItemsList,
  Navigation,
  StyledHistoryDialogContent,
  Title,
} from './styles';
import { createDialogCloseOnlyHandler } from '@/shared/libs/dialog-open-change';
import { useFormatAmount } from '@/shared/hooks/useFormatAmount';
import { useBodyScrollLock } from '@/shared/hooks/useBodyScrollLock';

interface CashbackHistoryItem {
  date: string;
  sum: string;
}

interface HistoryModalProps {
  visible: boolean;
  onClose: () => void;
}

const HistoryModal = ({ visible, onClose }: HistoryModalProps) => {
  const { data, isLoading } = useCashbackHistory();
  const locale = useCurrentLocale();
  const t = useScopedI18n('cashback');
  const formatAmount = useFormatAmount({ mode: 'plain' });

  useBodyScrollLock(visible);

  const formatDate = (date: string) => {
    return format(parseISO(date), 'dd MMMM', {
      locale: getDateLocale(locale),
    });
  };

  const formatSum = (sum: string) => {
    return formatAmount(sum);
  };

  if (isLoading) return null;

  return (
    <Dialog.Root
      open={visible}
      onOpenChange={createDialogCloseOnlyHandler(onClose)}
    >
      <Dialog.Portal>
        <StyledAlertDialogOverlay />
        <StyledHistoryDialogContent
          aria-describedby=""
          data-item="cashback_history"
        >
          <Navigation>
            <CloseIconImage
              src="/images/icons/close-dialog.svg"
              alt="close-dialog"
              onClick={() => onClose()}
            />
          </Navigation>

          <Title>
            <MediumHeadingQuinary>
              {t('history_of_cashback')}
            </MediumHeadingQuinary>
          </Title>

          <ItemsList>
            {data?.map((item: CashbackHistoryItem, index: number) => {
              const date = formatDate(item?.date);
              return (
                <Item
                  index={index}
                  total={data?.length}
                  key={item?.date + item?.sum}
                >
                  <HistoryIconImage
                    src="/images/icons/cashback-history-item-icon.svg"
                    alt="history-item"
                  />
                  <BodyTextBoldSecondary>{t('claimed')}</BodyTextBoldSecondary>

                  <Details>
                    <BodyTextBoldSecondary>
                      {formatSum(item?.sum)}
                    </BodyTextBoldSecondary>

                    <ShowOnMobile>
                      <DateStyleMobile>{date}</DateStyleMobile>
                    </ShowOnMobile>
                    <ShowOnDesktop>
                      <DateStyleDesktop>{date}</DateStyleDesktop>
                    </ShowOnDesktop>
                  </Details>
                </Item>
              );
            })}
          </ItemsList>
        </StyledHistoryDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export { HistoryModal };
